import * as React from "react";
import clsx from "clsx";
import sliderUnstyledClasses from "./sliderUnstyledClasses";

export interface SliderValueLabelUnstyledProps {
    children: React.ReactElement;
    className?: string;
    style?: React.CSSProperties;
    /**
     * The components used for each slot inside the ValueLabel.
     * Either a string to use a HTML element or a component.
     * @default {}
     */
    components?: {
        Root?: React.ElementType;
    };
    /**
     * If `true`, the value label is visible.
     */
    open: boolean;
    /**
     * Theme object for styled-components. Passed through to the label span.
     */
    theme?: Record<string, unknown>;
    /**
     * The value of the slider (can be formatted via valueLabelFormat).
     */
    value?: React.ReactNode;
    /**
     * Controls when the value label is displayed:
     *
     * - `auto` the value label will display when the thumb is hovered or focused.
     * - `on` will display persistently.
     * - `off` will never display.
     * @default 'off'
     */
    valueLabelDisplay?: "on" | "auto" | "off";
}

interface UseValueLabelClassesProps {
    open: boolean;
}

const useValueLabelClasses = (props: UseValueLabelClassesProps) => {
    const { open } = props;

    return {
        offset: clsx({
            [sliderUnstyledClasses.valueLabelOpen]: open,
        }),
        circle: sliderUnstyledClasses.valueLabelCircle,
        label: sliderUnstyledClasses.valueLabelLabel,
    };
};

/**
 * @ignore - internal component.
 */
export default function SliderValueLabelUnstyled(props: SliderValueLabelUnstyledProps) {
    const { children, className, value, theme } = props;
    const classes = useValueLabelClasses(props);

    const labelSpanProps: React.HTMLAttributes<HTMLSpanElement> & {
        theme?: Record<string, unknown>;
    } = {
        className: clsx(classes.offset, className),
        "aria-hidden": true,
    };
    if (theme !== undefined) {
        labelSpanProps.theme = theme;
    }

    return React.cloneElement(
        children,
        {
            className: clsx(children.props.className),
        },
        <React.Fragment>
            {children.props.children}
            <span {...labelSpanProps}>
                <span className={classes.circle}>
                    <span className={classes.label}>{value}</span>
                </span>
            </span>
        </React.Fragment>,
    );
}
